import { useMutation } from '@tanstack/react-query';
import { Play, RefreshCw } from 'lucide-react';
import { ActionButton } from '@/components/ui/ActionButton';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import type { QueueActionsProps } from '@/lib/types';

const SLUG = 'ap-purchase-invoice';

interface BulkRefreshResponse {
  total: number;
  posted: number;
  duplicates: number;
  advanced_to_multiple_grns: number;
  still_awaiting_approval: number;
  not_found?: number;
  failed: number;
}

function errorDetail(err: unknown, fallback: string): string {
  const raw = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') return JSON.stringify(raw);
  return (err as { message?: string })?.message ?? fallback;
}

function resultDescription(data: BulkRefreshResponse): string {
  const parts = [
    `${data.total} checked.`,
    `${data.posted} posted.`,
    `${data.advanced_to_multiple_grns} moved to Multiple GRNs.`,
    `${data.still_awaiting_approval} still awaiting approval.`,
  ];
  if (data.duplicates > 0) parts.push(`${data.duplicates} marked duplicate.`);
  if ((data.not_found ?? 0) > 0) parts.push(`${data.not_found} moved to GRN not found.`);
  if (data.failed > 0) parts.push(`${data.failed} failed.`);
  return parts.join(' ');
}

export function APBulkGrnRefreshAction({
  tab = 'open',
  facets,
  onComplete,
  disabled,
}: Partial<QueueActionsProps>) {
  const { toast } = useToast();
  const awaitingCount = facets?.bucket_counts?.GRN_UNAPPROVED;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<BulkRefreshResponse>(
        `/automations/${SLUG}/bulk/refresh-grn-candidates`,
        {},
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast({
        variant: data.failed > 0 ? 'warning' : 'success',
        title: 'Awaiting GRNs refreshed',
        description: resultDescription(data),
      });
      onComplete?.();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Refresh failed',
        description: errorDetail(err, 'Bulk GRN refresh failed'),
      });
    },
  });

  const scanMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ ok: boolean; status_code: number }>(
        `/automations/${SLUG}/run-inbox-scan`,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Invoice scan started',
        description: 'N8N accepted the inbox scan request.',
      });
      onComplete?.();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Scan failed',
        description: errorDetail(err, 'N8N inbox scan failed'),
      });
    },
  });

  if (tab !== 'open') return null;

  return (
    <>
      <ActionButton
        variant="secondary"
        size="sm"
        onClick={() => scanMutation.mutate()}
        leadingIcon={<Play className="h-16 w-16" />}
        loading={scanMutation.isPending}
        disabled={disabled}
        className="whitespace-nowrap"
      >
        Run invoice scan
      </ActionButton>
      <ActionButton
        variant="secondary"
        size="sm"
        onClick={() => mutation.mutate()}
        leadingIcon={<RefreshCw className="h-16 w-16" />}
        loading={mutation.isPending}
        disabled={disabled || awaitingCount === 0}
        className="whitespace-nowrap"
      >
        Refresh awaiting GRNs
      </ActionButton>
    </>
  );
}
